// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {
	PREMIUM_PRODUCT_FULL_NAME,
	PRODUCT_NAME,
	VIP_PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {
	FoundingMemberBadgeIcon,
	PrimeBadgeIcon,
	ProBadgeIcon,
} from '@app/features/user/components/badges/LafonBadgeIcons';
import {GuildBoostBadgeIcon} from '@app/features/guild/components/GuildBoostBadgeIcon';
import {GUILD_BOOST_MEMBER_LIST_TOOLTIP_DESCRIPTOR} from '@app/features/guild/utils/GuildBoostMessageDescriptors';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {handleExternalLinkClick} from '@app/features/ui/utils/NativeUtils';
import styles from '@app/features/user/components/popouts/UserProfileBadges.module.css';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import * as DateUtils from '@app/features/user/utils/DateFormatting';
import {formatVisionarySequenceLabel} from '@app/features/user/utils/VisionarySequence';
import {PublicUserFlags, UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

const BADGE_ASSET_BASE_URL = 'https://api.lafon.com.tr/badges';
const BADGE_ASSET_VERSION = '9';

const badgeAssetUrl = (fileName: string) => `${BADGE_ASSET_BASE_URL}/${fileName}?v=${BADGE_ASSET_VERSION}`;

const STAFF_DESCRIPTOR = msg({
	message: '{productName} Staff',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const PARTNER_DESCRIPTOR = msg({
	message: '{productName} Partner',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const BUG_HUNTER_DESCRIPTOR = msg({
	message: '{productName} Bug Hunter',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const VISIONARY_SINCE_DESCRIPTOR = msg({
	message: '{vipProductName} · {premiumSinceFormatted} tarihinden beri',
	comment:
		'Badge title with a date in the user profile badges popout. Preserve {vipProductName}, {premiumSinceFormatted}; they are inserted by code.',
});
const VISIONARY_DESCRIPTOR = msg({
	message: '{vipProductName}',
	comment: 'Short badge title for the lifetime VIP tier. Preserve {vipProductName}; it is inserted by code.',
});
const SUBSCRIBER_SINCE_DESCRIPTOR = msg({
	message: '{premiumProductFullName} · {premiumSinceFormatted} tarihinden beri',
	comment:
		'Badge label with a date in the user profile badges popout. Preserve {premiumProductFullName}, {premiumSinceFormatted}; they are inserted by code.',
});
const VISIONARY_ID_DESCRIPTOR = msg({
	message: 'Lafon ViP ID {visionaryIdLabel}',
	comment:
		'Short label in the user profile badges popout. Keep it concise. Preserve {visionaryIdLabel}; it is inserted by code.',
});
const FOUNDING_MEMBER_DESCRIPTOR = msg({
	message: '{sequenceLabel} - {productName} İlk 5000 Üyesi',
	comment:
		'Badge tooltip for founding members in the first 5000 accounts. Preserve {sequenceLabel} and {productName}; they are inserted by code. sequenceLabel is like #1.',
});

interface BaseBadge {
	key: string;
	tooltip: string;
	url?: string;
}

interface IconBadge extends BaseBadge {
	type: 'icon';
	iconUrl: string;
}

interface TextBadge extends BaseBadge {
	type: 'text';
	text: string;
}

interface ComponentBadge extends BaseBadge {
	type: 'component';
	renderIcon: (size: number, className: string) => React.ReactNode;
	tooltipMultiline?: boolean;
}

type Badge = IconBadge | TextBadge | ComponentBadge;

type BadgeGroup = 'all' | 'founding' | 'standard';

interface UserProfileBadgesProps {
	user: User;
	profile: Profile | null;
	isModal?: boolean;
	isMobile?: boolean;
	/** Inline next to username#tag. Overlay floats on the profile card. */
	placement?: 'inline' | 'overlay';
	/** founding = first-5000 only; standard = all other badges; all = both */
	group?: BadgeGroup;
	/** When set, shows the guild boost badge after the founding-member badge. */
	guildBoostSince?: Date | null;
}

const FOUNDING_BADGE_KEY = 'founding_member';
const GUILD_BOOST_BADGE_KEY = 'guild_boost';

function formatGuildBoostSinceDate(date: Date): string {
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();
	return `${day}.${month}.${year}`;
}

export const UserProfileBadges: React.FC<UserProfileBadgesProps> = observer(
	({user, profile, isModal = false, isMobile = false, placement, group = 'all', guildBoostSince = null}) => {
		const {i18n} = useLingui();
		const badges = useMemo(() => {
			const result: Array<Badge> = [];
			if (user.flags & PublicUserFlags.STAFF) {
				result.push({
					type: 'icon',
					key: 'staff',
					iconUrl: badgeAssetUrl('lafon-staff.svg'),
					tooltip: i18n._(STAFF_DESCRIPTOR, {productName: PRODUCT_NAME}),
					url: Routes.careers(),
				});
			}
			if (user.flags & PublicUserFlags.PARTNER) {
				result.push({
					type: 'icon',
					key: 'partner',
					iconUrl: badgeAssetUrl('partner.svg'),
					tooltip: i18n._(PARTNER_DESCRIPTOR, {productName: PRODUCT_NAME}),
					url: Routes.partners(),
				});
			}
			if (user.flags & PublicUserFlags.BUG_HUNTER) {
				result.push({
					type: 'icon',
					key: 'bug_hunter',
					iconUrl: badgeAssetUrl('bug-hunter.svg'),
					tooltip: i18n._(BUG_HUNTER_DESCRIPTOR, {productName: PRODUCT_NAME}),
					url: Routes.bugs(),
				});
			}
			if (profile?.premiumType && profile.premiumType !== UserPremiumTypes.NONE) {
				let tooltipText = PREMIUM_PRODUCT_FULL_NAME;
				let badgeUrl = Routes.plutonium();
				if (profile.premiumType === UserPremiumTypes.LIFETIME) {
					if (profile.premiumSince) {
						const premiumSinceFormatted = DateUtils.getFormattedShortDate(profile.premiumSince);
						tooltipText = i18n._(VISIONARY_SINCE_DESCRIPTOR, {
							vipProductName: VIP_PRODUCT_NAME,
							premiumSinceFormatted,
						});
					} else {
						tooltipText = i18n._(VISIONARY_DESCRIPTOR, {vipProductName: VIP_PRODUCT_NAME});
					}
					badgeUrl = Routes.helpArticle('visionary');
				} else if (profile.premiumSince) {
					const premiumSinceFormatted = DateUtils.getFormattedShortDate(profile.premiumSince);
					tooltipText = i18n._(SUBSCRIBER_SINCE_DESCRIPTOR, {
						premiumProductFullName: PREMIUM_PRODUCT_FULL_NAME,
						premiumSinceFormatted,
					});
				}
				const isLifetime = profile.premiumType === UserPremiumTypes.LIFETIME;
				result.push({
					type: 'component',
					key: 'premium',
					renderIcon: (size, className) =>
						isLifetime ? (
							<PrimeBadgeIcon className={className} size={size} />
						) : (
							<ProBadgeIcon className={className} size={size} />
						),
					tooltip: tooltipText,
					url: badgeUrl,
				});
				if (profile.premiumType === UserPremiumTypes.LIFETIME && profile.premiumLifetimeSequence != null) {
					const visionaryIdLabel = formatVisionarySequenceLabel(profile.premiumLifetimeSequence);
					result.push({
						type: 'text',
						key: 'premium_sequence',
						text: visionaryIdLabel,
						tooltip: i18n._(VISIONARY_ID_DESCRIPTOR, {visionaryIdLabel}),
						url: badgeUrl,
					});
				}
			}
			const foundingSequence = profile?.registrationSequence ?? user.registrationSequence ?? null;
			if (foundingSequence != null && foundingSequence >= 1 && foundingSequence <= 5000) {
				result.push({
					type: 'component',
					key: 'founding_member',
					renderIcon: (size, className) => <FoundingMemberBadgeIcon className={className} size={size} />,
					tooltip: i18n._(FOUNDING_MEMBER_DESCRIPTOR, {
						sequenceLabel: `#${foundingSequence}`,
						productName: PRODUCT_NAME,
					}),
				});
			}
			if (guildBoostSince != null && !Number.isNaN(guildBoostSince.getTime())) {
				result.push({
					type: 'component',
					key: GUILD_BOOST_BADGE_KEY,
					tooltip: i18n._(GUILD_BOOST_MEMBER_LIST_TOOLTIP_DESCRIPTOR, {
						date: formatGuildBoostSinceDate(guildBoostSince),
					}),
					tooltipMultiline: true,
					renderIcon: (size, className) => <GuildBoostBadgeIcon className={className} size={size} />,
				});
			}
			if (group === 'founding') {
				return result.filter(
					(badge) => badge.key === FOUNDING_BADGE_KEY || badge.key === GUILD_BOOST_BADGE_KEY,
				);
			}
			if (group === 'standard') {
				return result.filter(
					(badge) => badge.key !== FOUNDING_BADGE_KEY && badge.key !== GUILD_BOOST_BADGE_KEY,
				);
			}
			return result;
		}, [
			group,
			guildBoostSince,
			user.flags,
			user.registrationSequence,
			profile?.registrationSequence,
			profile?.premiumType,
			profile?.premiumSince,
			profile?.premiumLifetimeSequence,
			i18n.locale,
		]);
		if (badges.length === 0) {
			return null;
		}
		const resolvedPlacement = placement ?? (isModal ? 'inline' : 'overlay');
		const containerClassName =
			resolvedPlacement === 'inline'
				? clsx(
						styles.containerInline,
						isMobile ? styles.containerInlineMobile : styles.containerInlineDesktop,
					)
				: styles.containerOverlay;
		const badgeClassName = isMobile ? styles.badgeMobile : styles.badgeDesktop;
		const badgeSize = isMobile ? 28 : 20;
		const isDesktopInteractions = !isMobile;
		const renderInteractiveWrapper = (url: string | undefined, children: React.ReactNode) => {
			if (url && isDesktopInteractions) {
				return (
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.link}
						onClick={(event) => handleExternalLinkClick(event, url)}
						data-flx="user.user-profile-badges.render-interactive-wrapper.link"
					>
						{children}
					</a>
				);
			}
			return (
				<div className={styles.link} data-flx="user.user-profile-badges.render-interactive-wrapper.link--2">
					{children}
				</div>
			);
		};
		return (
			<div className={containerClassName} data-flx="user.user-profile-badges.div">
				{badges.map((badge) => {
					const sequenceClassName = isMobile ? styles.sequenceBadgeMobile : styles.sequenceBadgeDesktop;
					const badgeContent =
						badge.type === 'icon' ? (
							<img
								src={badge.iconUrl}
								alt={badge.tooltip}
								className={badgeClassName}
								data-flx="user.user-profile-badges.img"
							/>
						) : badge.type === 'component' ? (
							<span className={clsx(styles.componentBadge, badgeClassName)} data-flx="user.user-profile-badges.component">
								{badge.renderIcon(badgeSize, styles.componentBadgeIcon)}
							</span>
						) : (
							<span
								className={clsx(styles.sequenceBadge, sequenceClassName)}
								aria-hidden="true"
								data-flx="user.user-profile-badges.sequence-badge"
							>
								{badge.text}
							</span>
						);
					const tooltipText =
						badge.type === 'component' && badge.tooltipMultiline
							? () => <span className={styles.multilineTooltip}>{badge.tooltip}</span>
							: badge.tooltip;
					return (
						<Tooltip key={badge.key} text={tooltipText} maxWidth="xl" data-flx="user.user-profile-badges.tooltip">
							<FocusRing offset={-2} data-flx="user.user-profile-badges.focus-ring">
								{renderInteractiveWrapper(badge.url, badgeContent)}
							</FocusRing>
						</Tooltip>
					);
				})}
			</div>
		);
	},
);
